import React, { useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import {
  Box,
  Chip,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Autocomplete,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  SelectChangeEvent,
  Collapse,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import ReplayIcon from "@mui/icons-material/Replay";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import developmentsData from "../../resident-portal";
import {
  useGetUsersQuery,
  UserWithRelations,
  useGetPropertiesQuery,
  Property,
  PaymentData,
  PaymentWithRelations,
  residentPortalApi,
  usePostPaymentsAdminMutation,
  useGetInternetPackagesQuery,
  useGetPaymentsAdminQuery,
  usePostPaymentsByIdRetryTopupMutation,
  PostPaymentsByIdRetryTopupApiResponse,
} from "../../redux/residentPortalApi";
import { useAppDispatch } from "../../redux/hooks";
import { formatCurrency } from "../../utils/currencyUtil";
import { formatPropertyNumber } from "../../utils/propertyUtils";
import { capitalise } from "../../utils/stringUtil";
import logCashPaymentModel, { logCashPaymentModelType } from "../forms/models/logCashPaymentModel";
import logCashPaymentSchema from "../forms/schemas/logCashPaymentSchema";

type MyFormValues = logCashPaymentModelType;
type PaymentFilters = {
  name: string;
  email: string;
  purpose: string;
  paymentMethod: string;
  receiptNumber: string;
  paymentDate: string;
  fromDate: string;
  toDate: string;
  development: string;
  phase: string;
  type: string;
  reconciliationFlow: string;
};

type PaymentMetadata = {
  reconciliationAudit?: {
    reconciled?: boolean;
    actorName?: string;
    reconciledAt?: string;
  };
};

const logCashPaymentModelInitial = logCashPaymentModel.initialValues;
const LogCashPaymentFormSchema = logCashPaymentSchema;
const formatOptionLabel = (value: string) =>
  value
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const LogManualPayment: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: users } = useGetUsersQuery();
  const { data: properties } = useGetPropertiesQuery();
  const { data: internetPackages } = useGetInternetPackagesQuery();
  const [adminPaymentCreate] = usePostPaymentsAdminMutation();
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState<MyFormValues | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isExporting, setIsExporting] = useState(false);
  const [retryPaymentId, setRetryPaymentId] = useState<string | null>(null);
  const [retryResult, setRetryResult] = useState<PostPaymentsByIdRetryTopupApiResponse | null>(null);
  const [retryError, setRetryError] = useState<string>("");
  const [filters, setFilters] = useState<PaymentFilters>({
    name: "",
    email: "",
    purpose: "",
    paymentMethod: "",
    receiptNumber: "",
    paymentDate: "",
    fromDate: "",
    toDate: new Date().toISOString().split('T')[0],
    development: "",
    phase: "",
    type: "",
    reconciliationFlow: "",
  });

  const propertyMetrics = developmentsData.propertyMetrics ?? [];
  const developmentOptions = Array.from(new Set(propertyMetrics.map((metric) => metric.development)));
  const phaseOptions = Array.from(
    new Set(
      propertyMetrics
        .filter((metric) => !filters.development || metric.development === filters.development)
        .map((metric) => metric.phase),
    ),
  );
  const typeOptions = Array.from(
    new Set(
      propertyMetrics
        .filter((metric) => {
          const matchesDevelopment = !filters.development || metric.development === filters.development;
          const matchesPhase = !filters.phase || metric.phase === filters.phase;
          return matchesDevelopment && matchesPhase;
        })
        .map((metric) => metric.type),
    ),
  );

  const purposes = [
    { id: "gas", label: "Gas" },
    { id: "water", label: "Water" },
    { id: "electricity", label: "Electricity" },
    { id: "levies", label: "Levies" },
    { id: "internet", label: "Internet" },
    { id: "pedalCourtInvoiceBalance", label:"Padel Court Special Levy"}
  ];

  const paymentMethods = ["POS", "Cash", "Bank Transfer", "EcoCash", "Card"];

  const { data: payments = [], isFetching: paymentsLoading } = useGetPaymentsAdminQuery({
    name: filters.name,
    email: filters.email,
    purpose: filters.purpose,
    paymentMethod: filters.paymentMethod,
    paymentIntentId: filters.receiptNumber,
    paymentDate: filters.paymentDate,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    development: filters.development,
    phase: filters.phase,
    type: filters.type,
    limit: pageSize,
    skip: page * pageSize,
  });
  const [retryTopUp, { isLoading: retryLoading }] = usePostPaymentsByIdRetryTopupMutation();

  const filteredPaymentsByReconciliationFlow = useMemo(() => {
    if (!filters.reconciliationFlow) {
      return payments;
    }

    return payments.filter((payment: PaymentWithRelations) => {
      const metadata = (payment.metadata ?? {}) as PaymentMetadata;
      const wasAgentReconciled = metadata.reconciliationAudit?.reconciled === true;

      if (filters.reconciliationFlow === "ai-inspired") {
        return wasAgentReconciled;
      }

      if (filters.reconciliationFlow === "standard-flow") {
        return !wasAgentReconciled;
      }

      return true;
    });
  }, [payments, filters.reconciliationFlow]);

  const filteredProperties = properties?.filter((property: Property) =>
    selectedClient ? property?.ownerIds?.includes(selectedClient) : false,
  ) || [];

  const handleFilterChange = (field: keyof PaymentFilters, value: string) => {
    setPage(0);
    setFilters((prevFilters) => {
      if (field === "development") {
        return {
          ...prevFilters,
          development: value,
          phase: "",
          type: "",
        };
      }

      if (field === "phase") {
        return {
          ...prevFilters,
          phase: value,
          type: "",
        };
      }

      return {
        ...prevFilters,
        [field]: value,
      };
    });
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(0);
  };

  const handleSubmit = async (values: MyFormValues) => {
    const paymentDateTime = values.paymentDate
      ? new Date(values.paymentDate).toISOString()
      : new Date().toISOString();

    const data = {
      amount: values?.amount,
      currency: "usd",
      status: "succeeded",
      purpose: values.purpose,
      paymentMethod: values.paymentMethod,
      propertyId: values.property,
      userId: values.client,
      paymentIntentId: values.receiptNumber,
      paymentDate: paymentDateTime,
    } as PaymentData;

    await adminPaymentCreate({ paymentData: data }).unwrap();
    setSubmittedData(values);
    setSuccess(true);
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const rows = await dispatch(
        residentPortalApi.endpoints.getPaymentsAdmin.initiate(
          {
            name: filters.name,
            email: filters.email,
            purpose: filters.purpose,
            paymentMethod: filters.paymentMethod,
            paymentIntentId: filters.receiptNumber,
            paymentDate: filters.paymentDate,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            development: filters.development,
            phase: filters.phase,
            type: filters.type,
            limit: -1,
            skip: 0,
          },
          { subscribe: false, forceRefetch: true },
        ),
      ).unwrap();

      if (!rows?.length) {
        window.alert("No payments found for the selected filters.");
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(
        rows.map((payment) => ({
          "Payment Date": payment.paymentDate ? dayjs(payment.paymentDate).format("DD/MM/YYYY") : "",
          Name: payment.user?.name ?? "",
          Email: payment.user?.email ?? "",
          Purpose: capitalise(payment.purpose ?? ""),
          "Payment Method": capitalise(payment.paymentMethod ?? ""),
          "Receipt Number": payment.paymentIntentId ?? "",
          Development: payment.property?.development ?? "",
          Phase: payment.property?.phase ?? "",
          Type: payment.property?.type ?? "",
          "Unit Number": payment.property?.unitNumber ?? "",
          Amount: (payment.amount ?? 0) / 100,
          Reference: payment.paymentIntentId ?? "",
        })),
      );

      worksheet["!cols"] = [
        { wch: 16 },
        { wch: 22 },
        { wch: 28 },
        { wch: 16 },
        { wch: 18 },
        { wch: 18 },
        { wch: 14 },
        { wch: 14 },
        { wch: 12 },
        { wch: 12 },
        { wch: 20 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Payments");
      XLSX.writeFile(workbook, `payments-report-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error("Failed to export payments", error);
      window.alert("Failed to export the payments report. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleRetryTopUp = async (paymentId?: string) => {
    if (!paymentId) {
      return;
    }

    setRetryError("");
    setRetryResult(null);
    setRetryPaymentId(paymentId);

    try {
      const result = await retryTopUp({ id: paymentId }).unwrap();
      setRetryResult(result);
    } catch (error) {
      console.error("Failed to retry token top-up", error);
      const message = error instanceof Error
        ? error.message
        : "Failed to retry token top-up. Check backend logs for more detail.";
      setRetryError(message);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 5, px: 2 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Log Manual Payment
      </Typography>

      <Paper elevation={3} sx={{ padding: 4, width: "100%", maxWidth: 700, borderRadius: 3, mb: 4 }}>
        {success ? (
          <Box sx={{ textAlign: "center" }}>
            <CheckCircleIcon sx={{ color: "green", fontSize: 80, marginBottom: 2 }} />
            <Typography variant="h5" color="green" gutterBottom>
              Payment Logged Successfully!
            </Typography>
            <Box sx={{ background: "#f4f4f4", padding: 2, borderRadius: 2, textAlign: "left" }}>
              <Typography variant="h6">Submitted Details:</Typography>
              <Typography><strong>Client ID:</strong> {submittedData?.client}</Typography>
              <Typography><strong>Property ID:</strong> {submittedData?.property}</Typography>
              <Typography><strong>Amount:</strong> ${submittedData?.amount}</Typography>
              <Typography><strong>Payment Method:</strong> {submittedData?.paymentMethod}</Typography>
              <Typography><strong>Purpose:</strong> {submittedData?.purpose}</Typography>
              <Typography><strong>Payment Date:</strong> {submittedData?.paymentDate}</Typography>
              <Typography><strong>Receipt Number:</strong> {submittedData?.receiptNumber}</Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              sx={{ marginTop: 3 }}
              onClick={() => {
                setSuccess(false);
                setSubmittedData(null);
              }}
            >
              Log Another Payment
            </Button>
          </Box>
        ) : (
          <Formik initialValues={logCashPaymentModelInitial} onSubmit={handleSubmit} validationSchema={LogCashPaymentFormSchema}>
            {({ values, handleChange, setFieldValue }) => (
              <Form>
                <Autocomplete
                  options={users || []}
                  getOptionLabel={(option: UserWithRelations) => option.name ? `${option.name} (${option.email})` : option.email ?? ""}
                  onChange={(_, value) => {
                    const clientId = value ? value.id : "";
                    setSelectedClient(clientId ?? "");
                    setFieldValue("client", clientId);
                    setFieldValue("property", "");
                  }}
                  renderInput={(params) => <TextField {...params} label="Client" fullWidth margin="normal" />}
                />

                <Autocomplete
                  options={filteredProperties}
                  getOptionLabel={(option: Property) => formatPropertyNumber(option)}
                  onChange={(_, value) => {
                    setFieldValue("property", value ? value.id : "");
                    if (values.purpose !== "internet") {
                      return;
                    }

                    const selectedPropertyPackage = internetPackages?.find(
                      (pkg) => pkg.id && pkg.id === value?.internetPackageId,
                    );

                    if (selectedPropertyPackage?.id) {
                      setFieldValue("internetPackageId", selectedPropertyPackage.id);
                      setFieldValue("amount", selectedPropertyPackage.price);
                    } else {
                      setFieldValue("internetPackageId", "");
                      setFieldValue("amount", null);
                    }
                  }}
                  disabled={!selectedClient}
                  renderInput={(params) => <TextField {...params} label="Property" fullWidth margin="normal" />}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Amount (US$)"
                  type="number"
                  name="amount"
                  value={values.amount ?? ""}
                  onChange={handleChange}
                  InputProps={{
                    readOnly: values.purpose === "internet",
                  }}
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Payment Method</InputLabel>
                  <Field as={Select} name="paymentMethod" value={values.paymentMethod} onChange={handleChange} label="Payment Method">
                    {paymentMethods.map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Field>
                </FormControl>

                <TextField fullWidth margin="normal" label="Receipt Number" name="receiptNumber" value={values.receiptNumber} onChange={handleChange} />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Purpose</InputLabel>
                  <Field
                    as={Select}
                    name="purpose"
                    value={values.purpose}
                    onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                      const selectedPurpose = event.target.value as string;
                      setFieldValue("purpose", selectedPurpose);

                      if (selectedPurpose !== "internet") {
                        setFieldValue("internetPackageId", "");
                        return;
                      }

                      const selectedProperty = filteredProperties.find(
                        (property) => property.id === values.property,
                      );

                      const selectedPropertyPackage = internetPackages?.find(
                        (pkg) => pkg.id && pkg.id === selectedProperty?.internetPackageId,
                      );

                      if (selectedPropertyPackage?.id) {
                        setFieldValue("internetPackageId", selectedPropertyPackage.id);
                        setFieldValue("amount", selectedPropertyPackage.price);
                      } else {
                        setFieldValue("internetPackageId", "");
                        setFieldValue("amount", null);
                      }
                    }}
                    label="Purpose"
                  >
                    {purposes.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                    ))}
                  </Field>
                </FormControl>

                {values.purpose === "internet" && (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Internet Package</InputLabel>
                    <Field
                      as={Select}
                      name="internetPackageId"
                      value={values.internetPackageId}
                      onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                        const packageId = event.target.value as string;
                        setFieldValue("internetPackageId", packageId);

                        const selectedPackage = internetPackages?.find((pkg) => pkg.id === packageId);
                        setFieldValue("amount", selectedPackage?.price ?? null);
                      }}
                      label="Internet Package"
                    >
                      {(internetPackages || []).map((pkg) => (
                        <MenuItem key={pkg.id} value={pkg.id}>{pkg.name}</MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  margin="normal"
                  label="Payment Date"
                  type="date"
                  name="paymentDate"
                  value={values.paymentDate}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />

                <Button type="submit" variant="contained" color="primary" fullWidth sx={{ marginTop: 2 }}>
                  Submit
                </Button>
              </Form>
            )}
          </Formik>
        )}
      </Paper>

      <Paper elevation={3} sx={{ padding: 4, width: "100%", maxWidth: 1200, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Payments Table
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Filter payments by user details, payment details, and property hierarchy. Export returns all matching rows.
        </Typography>

        <Box display="flex" gap={2} alignItems="center" width="100%" flexWrap="wrap" mb={3}>
          <TextField
            label="Name"
            size="small"
            value={filters.name}
            onChange={(e) => handleFilterChange("name", e.target.value)}
            sx={{ flex: "1 1 180px" }}
          />
          <TextField
            label="Email"
            size="small"
            value={filters.email}
            onChange={(e) => handleFilterChange("email", e.target.value)}
            sx={{ flex: "1 1 220px" }}
          />
          <TextField
            select
            label="Purpose"
            size="small"
            value={filters.purpose}
            onChange={(e) => handleFilterChange("purpose", e.target.value)}
            sx={{ flex: "1 1 160px" }}
          >
            <MenuItem value="">All</MenuItem>
            {purposes.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Payment Method"
            size="small"
            value={filters.paymentMethod}
            onChange={(e) => handleFilterChange("paymentMethod", e.target.value)}
            sx={{ flex: "1 1 180px" }}
          >
            <MenuItem value="">All</MenuItem>
            {paymentMethods.map((method) => (
              <MenuItem key={method} value={method}>{method}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Receipt Number"
            size="small"
            value={filters.receiptNumber}
            onChange={(e) => handleFilterChange("receiptNumber", e.target.value)}
            sx={{ flex: "1 1 180px" }}
          />
          <TextField
            select
            label="Development"
            size="small"
            value={filters.development}
            onChange={(e) => handleFilterChange("development", e.target.value)}
            sx={{ flex: "1 1 180px" }}
          >
            <MenuItem value="">All</MenuItem>
            {developmentOptions.map((development) => (
              <MenuItem key={development} value={development}>{formatOptionLabel(development)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Phase"
            size="small"
            value={filters.phase}
            onChange={(e) => handleFilterChange("phase", e.target.value)}
            disabled={!filters.development}
            sx={{ flex: "1 1 180px" }}
          >
            <MenuItem value="">All</MenuItem>
            {phaseOptions.map((phase) => (
              <MenuItem key={phase} value={phase}>{formatOptionLabel(phase)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Type"
            size="small"
            value={filters.type}
            onChange={(e) => handleFilterChange("type", e.target.value)}
            disabled={!filters.phase}
            sx={{ flex: "1 1 180px" }}
          >
            <MenuItem value="">All</MenuItem>
            {typeOptions.map((propertyType) => (
              <MenuItem key={propertyType} value={propertyType}>{formatOptionLabel(propertyType)}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Payment Date"
            type="date"
            size="small"
            value={filters.paymentDate}
            onChange={(e) => handleFilterChange("paymentDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: "1 1 180px" }}
          />
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={filters.fromDate}
            onChange={(e) => handleFilterChange("fromDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: "1 1 180px" }}
          />
          <TextField
            label="To Date"
            type="date"
            size="small"
            value={filters.toDate}
            onChange={(e) => handleFilterChange("toDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: "1 1 180px" }}
          />
          <TextField
            select
            label="Reconciliation"
            size="small"
            value={filters.reconciliationFlow}
            onChange={(e) => handleFilterChange("reconciliationFlow", e.target.value)}
            sx={{ flex: "1 1 180px" }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="standard-flow">Standard flow</MenuItem>
            <MenuItem value="ai-inspired">AI-inspired</MenuItem>
          </TextField>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={isExporting || paymentsLoading}
            sx={{ minWidth: 180, height: 40 }}
          >
            {isExporting ? "Exporting..." : "Export Excel"}
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Purpose</strong></TableCell>
                <TableCell><strong>Method</strong></TableCell>
                <TableCell><strong>Development</strong></TableCell>
                <TableCell><strong>Phase</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Unit</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell><strong>Reconciliation</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPaymentsByReconciliationFlow.length ? filteredPaymentsByReconciliationFlow.map((payment: PaymentWithRelations) => {
                const metadata = (payment.metadata ?? {}) as PaymentMetadata;
                const reconciliationAudit = metadata.reconciliationAudit;
                const wasAgentReconciled = reconciliationAudit?.reconciled === true;

                return (
                <React.Fragment key={payment.id}>
                  <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}>
                    <TableCell>{payment.paymentDate ? dayjs(payment.paymentDate).format("DD/MM/YYYY") : "N/A"}</TableCell>
                    <TableCell>{payment.user?.name ?? "N/A"}</TableCell>
                    <TableCell>{payment.user?.email ?? "N/A"}</TableCell>
                    <TableCell>{capitalise(payment.purpose ?? "")}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{capitalise(payment.paymentMethod ?? "")}</Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          Receipt: {payment.paymentIntentId ?? "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{payment.property?.development ?? "N/A"}</TableCell>
                    <TableCell>{payment.property?.phase ?? "N/A"}</TableCell>
                    <TableCell>{payment.property?.type ?? "N/A"}</TableCell>
                    <TableCell>{payment.property?.unitNumber ?? "N/A"}</TableCell>
                    <TableCell align="right">{formatCurrency((payment.amount ?? 0) / 100)}</TableCell>
                    <TableCell>
                      {wasAgentReconciled ? (
                        <Box>
                          <Chip
                            size="small"
                            color="warning"
                            label={capitalise(reconciliationAudit?.actorName ?? "Inspired AI Agent")}
                          />
                          {reconciliationAudit?.reconciledAt && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              {dayjs(reconciliationAudit.reconciledAt).format("DD/MM/YYYY HH:mm")}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Standard flow
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReplayIcon />}
                        disabled={!payment.id || retryLoading}
                        onClick={() => handleRetryTopUp(payment.id)}
                      >
                        {retryLoading && retryPaymentId === payment.id ? "Retrying..." : "Retry Top-up"}
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={12} sx={{ py: 0, borderBottom: retryPaymentId === payment.id ? undefined : 0 }}>
                      <Collapse in={retryPaymentId === payment.id && (!!retryResult || !!retryError)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, backgroundColor: "rgba(0,0,0,0.03)" }}>
                          {retryError ? (
                            <Typography color="error.main" variant="body2">{retryError}</Typography>
                          ) : null}
                          {retryResult ? (
                            <Box>
                              <Typography variant="subtitle2" gutterBottom>Retry Top-up Result</Typography>
                              <Typography variant="body2"><strong>Status:</strong> {retryResult.topUpStatus ?? "N/A"}</Typography>
                              <Typography variant="body2"><strong>Order ID:</strong> {retryResult.orderId ?? "N/A"}</Typography>
                              <Typography variant="body2"><strong>Token:</strong> {retryResult.token ?? "N/A"}</Typography>
                              <Typography variant="body2"><strong>Token First:</strong> {retryResult.tokenFirst ?? "N/A"}</Typography>
                              <Typography variant="body2"><strong>Token Second:</strong> {retryResult.tokenSecond ?? "N/A"}</Typography>
                              <Typography variant="body2"><strong>Receipt ID:</strong> {String(retryResult.receiptId ?? "N/A")}</Typography>
                              <Typography variant="body2"><strong>Remote Submitted:</strong> {String(retryResult.remoteSubmitted ?? false)}</Typography>
                              <Typography variant="body2"><strong>Remote Task ID:</strong> {retryResult.remoteTaskId ?? "N/A"}</Typography>
                              <Typography variant="body2"><strong>Meter Provider:</strong> {retryResult.meterProvider ?? "N/A"}</Typography>
                              <Typography variant="body2"><strong>Meter ID:</strong> {retryResult.meterId ?? "N/A"}</Typography>
                              <Typography variant="subtitle2" sx={{ mt: 2 }}>Debug Payload</Typography>
                              <Box
                                component="pre"
                                sx={{
                                  mt: 1,
                                  mb: 0,
                                  p: 2,
                                  borderRadius: 2,
                                  backgroundColor: "#111827",
                                  color: "#e5e7eb",
                                  overflowX: "auto",
                                  fontSize: 12,
                                }}
                              >
                                {JSON.stringify(retryResult.metaInfo ?? {}, null, 2)}
                              </Box>
                            </Box>
                          ) : null}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
                );
              }) : (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography sx={{ py: 2, color: "text.secondary" }}>
                      {paymentsLoading ? "Loading payments..." : "No payments found for the selected filters."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="center" alignItems="center" mt={2} gap={2} flexWrap="wrap">
          <Button onClick={() => setPage(Math.max(page - 1, 0))} disabled={page === 0} variant="contained">
            Previous
          </Button>
          <Typography variant="body1">Page {page + 1}</Typography>
          <Button onClick={() => setPage(page + 1)} disabled={payments.length < pageSize} variant="contained">
            Next
          </Button>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Rows</InputLabel>
            <Select value={pageSize} label="Rows" onChange={handlePageSizeChange}>
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>
    </Box>
  );
};

export default LogManualPayment;
